import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { StatusIndicator } from '../StatusIndicator';
import { ColumnArrow } from '../ColumnArrow';
import { ConnectedThermostatModal } from './components';
import { ConnectedThermostatRestore } from './components';
import { ThermostatReauthentication } from './components';
import { PrimaryButton } from '../PrimaryButton';
import { Spinner } from '../Spinner';
import { Pagination } from '../Pagination';
import { Error404Page } from '../Error404Page';
import { ThermostatActions, NetworkActions } from '../../store';
import './ThermostatSetup.css';

const directionShorten = {
  north: 'N',
  west: 'W',
  east: 'E',
  south: 'S',
  north_west: 'NW',
  north_east: 'NE',
  south_west: 'SW',
  south_east: 'SE',
};

export class ThermostatSetup extends Component {
  state = {
    currentPage: 1,
    filteredThermostatsList: [],
    filters: { sortValue: 'ZONE', isReversed: false },
    currentThermostat: undefined,
    isAddModalOpen: false,
    isModifyModalOpen: false,
  };

  componentDidMount() {
    const {
      selectedBuilding,
      navigationParams: { selectedSubCategory },
      setThermostats,
    } = this.props;

    setThermostats(selectedBuilding.slug, selectedSubCategory.id);
  }

  componentWillUnmount() {
    this.props.clearLastRequest();
  }

  openModal = () => {
    this.setState((state) => ({
      ...state,
      isAddModalOpen: true,
    }));
  };

  closeModal = () => {
    const { selectedBuilding } = this.props;

    this.props.clearRefreshedPin();

    this.setState((state) => ({
      ...state,
      isAddModalOpen: false,
    }));

    this.props.setThermostats(selectedBuilding.slug);
  };

  updateSortValue(sortValue) {
    const isReversed = sortValue === this.state.filters.sortValue && !this.state.filters.isReversed;
    this.setState(
      (state) => ({
        ...state,
        filters: {
          ...state.filters,
          isReversed,
          sortValue,
        },
      }),
      () => this.updateFilteredThermostats(),
    );
  }

  updateFilteredThermostats() {
    const { filters, filteredThermostatsList = [] } = this.state;
    const { sortValue, isReversed } = filters;
    const { allThermostats } = this.props;
    let tempThermostatList = filteredThermostatsList.length > 0 ? filteredThermostatsList : allThermostats;

    switch (sortValue) {
      case 'ZONE':
        // if this is the first time sorting, or if the previous sort is not by ZONE
        if (!isReversed) {
          tempThermostatList = tempThermostatList.sort((a, b) => {
            if (a.unit_number < b.unit_number) return -1;
            if (a.unit_number > b.unit_number) return 1;
            return 0;
          });
          break;
        }
        // previous sort is also by ZONE, we will sort in reverse
        tempThermostatList = tempThermostatList.sort((a, b) => {
          if (a.unit_number > b.unit_number) return -1;
          if (a.unit_number < b.unit_number) return 1;
          return 0;
        });
        break;
      case 'STATUS':
        // if this is the first time sorting, or if the previous sort is not by STATUS
        if (!isReversed) {
          tempThermostatList = tempThermostatList.sort((a, b) => {
            if (a.connection_state < b.connection_state) return -1;
            if (a.connection_state > b.connection_state) return 1;
            return 0;
          });
          break;
        }
        // previous sort is also by ZONE, we will sort in reverse
        tempThermostatList = tempThermostatList.sort((a, b) => {
          if (a.connection_state > b.connection_state) return -1;
          if (a.connection_state < b.connection_state) return 1;
          return 0;
        });
        break;
      case 'SERIAL_NUMBER':
        // if this is the first time sorting, or if the previous sort is not by SERIAL NUMBER
        if (!isReversed) {
          tempThermostatList = tempThermostatList.sort((a, b) => {
            if (a.serial_number < b.serial_number) return -1;
            if (a.serial_number > b.serial_number) return 1;
            return 0;
          });
          break;
        }
        // previous sort is also by ZONE, we will sort in reverse
        tempThermostatList = tempThermostatList.sort((a, b) => {
          if (a.serial_number > b.serial_number) return -1;
          if (a.serial_number < b.serial_number) return 1;
          return 0;
        });
        break;
      case 'REGISTERED_DATE':
        // if this is the first time sorting, or if the previous sort is not by REGISTERED DATE
        if (!isReversed) {
          tempThermostatList = tempThermostatList.sort((a, b) => {
            if (a.connection_timestamp < b.connection_timestamp) return -1;
            if (a.connection_timestamp > b.connection_timestamp) return 1;
            return 0;
          });
          break;
        }
        // previous sort is also by REGISTERED DATE, we will sort in reverse
        tempThermostatList = tempThermostatList.sort((a, b) => {
          if (a.connection_timestamp > b.connection_timestamp) return -1;
          if (a.connection_timestamp < b.connection_timestamp) return 1;
          return 0;
        });
        break;
      default:
        tempThermostatList = allThermostats;
    }

    this.setState((state) => ({
      ...state,
      filters,
      filteredThermostatsList: tempThermostatList,
    }));
  }

  restoreThermostat(thermostat) {
    this.setState((state) => ({
      ...state,
      isModifyModalOpen: true,
      currentThermostat: thermostat,
    }));
  }

  closeRestoreModal = () => {
    this.props.clearRefreshedPin();
    this.props.clearRestoreResults();

    this.setState((state) => ({
      ...state,
      isModifyModalOpen: false,
      currentThermostat: undefined,
    }));
  };

  changePage = (num) => {
    this.setState((state) => ({
      ...state,
      currentPage: num,
    }));
  };

  renderThermostats() {
    const { currentPage } = this.state;
    const { allThermostats } = this.props;

    // logic for displaying thermostats
    const thermostatsPerPage = window.innerWidth > 599 ? 10 : 7;
    const indexOfLastThermostat = currentPage * thermostatsPerPage;
    const indexOfFirstThermostat = indexOfLastThermostat - thermostatsPerPage;
    const currentThermostats = allThermostats.slice(indexOfFirstThermostat, indexOfLastThermostat);

    return (
      <div className="thermostat-items">
        {currentThermostats.map((thermostat) => {
          const formattedRegisteredDate =
            thermostat.connection_timestamp === null
              ? 'N/A'
              : Moment(thermostat.connection_timestamp).format('MMM DD hh:mmA');
          const formattedDisconnectedDate =
            thermostat.disconnection_timestamp === null
              ? 'N/A'
              : Moment(thermostat.disconnection_timestamp).format('MMM DD hh:mmA');
          return (
            <button
              className="thermostat-setup-legend__thermostat-item"
              key={thermostat.uid_hash}
              onClick={() => this.restoreThermostat(thermostat)}
            >
              <div className="equal-width">{thermostat.unit_number + directionShorten[thermostat.direction]}</div>
              <div className="equal-width status-column">
                <div className="status-column--status">
                  <StatusIndicator state={thermostat.connection_state} />
                </div>
                <div className="status-column--text">{thermostat.connection_state}</div>
              </div>
              <div className="equal-width desktop-only">{thermostat.serial_number}</div>
              <div className="equal-width desktop-only">{formattedRegisteredDate}</div>
              <div className="equal-width desktop-only">{formattedDisconnectedDate}</div>
            </button>
          );
        })}
      </div>
    );
  }

  renderModals() {
    const isReset = this.state.currentThermostat && this.state.currentThermostat.connection_state === 'reset';
    const {
      selectedBuilding: { name: buildingName },
    } = this.props;
    const { isAddModalOpen, isModifyModalOpen, currentThermostat } = this.state;

    if (isAddModalOpen) {
      return <ConnectedThermostatModal onRequestClose={this.closeModal} />;
    }

    if (isModifyModalOpen && !isReset) {
      return (
        <ConnectedThermostatRestore
          onRequestClose={this.closeRestoreModal}
          selectedBuilding={buildingName}
          currentThermostat={currentThermostat}
        />
      );
    }
    if (isModifyModalOpen && isReset) {
      return (
        <ThermostatReauthentication
          onRequestClose={this.closeRestoreModal}
          selectedBuilding={buildingName}
          currentThermostat={currentThermostat}
        />
      );
    }
    return null;
  }

  render() {
    const { sortValue, isReversed } = this.state.filters;
    const legendColumnClass = 'thermostat-setup-legend__column-name';
    const { allThermostats } = this.props;
    const { invalidSelectedBuildingError, isFetching } = this.props;
    const thermostatsPerPage = window.innerWidth > 599 ? 10 : 7;

    if (invalidSelectedBuildingError) {
      return <Error404Page />;
    }

    return (
      <React.Fragment>
        {this.renderModals()}
        <div className="thermostat-setup-legend">
          <div className="thermostat-setup-legend__header">
            <div className="thermostat-setup-legend__title">All Thermostats</div>
            <div className="desktop-only">
              <PrimaryButton
                id="add-new--desktop"
                icon="add-icon"
                label="Add New Thermostat"
                onClick={this.openModal}
                fullWidth={true}
              />
            </div>
            <div className="mobile-only">
              <PrimaryButton id="add-new--mobile" icon="add-icon" label="Add New" onClick={this.openModal} />
            </div>
          </div>
          <div className="thermostat-setup-legend__column-names">
            <button
              className={
                sortValue === 'ZONE'
                  ? `${legendColumnClass} thermostat-setup-legend__column-name--selected`
                  : legendColumnClass
              }
              id="zone"
              onClick={() => this.updateSortValue('ZONE')}
            >
              <span className="thermostat-setup-legend__column-name-text">ZONE</span>
              <ColumnArrow value="ZONE" sortValue={sortValue} isReversed={isReversed} />
            </button>
            <button
              className={
                sortValue === 'STATUS'
                  ? `${legendColumnClass} thermostat-setup-legend__column-name--selected`
                  : legendColumnClass
              }
              id="status"
              onClick={() => this.updateSortValue('STATUS')}
            >
              <span className="thermostat-setup-legend__column-name-text">STATUS</span>
              <ColumnArrow value="STATUS" sortValue={sortValue} isReversed={isReversed} />
            </button>
            <button
              className={
                sortValue === 'SERIAL_NUMBER'
                  ? `${legendColumnClass} thermostat-setup-legend__column-name--selected desktop-only`
                  : `${legendColumnClass} desktop-only`
              }
              id="serial-number"
              onClick={() => this.updateSortValue('SERIAL_NUMBER')}
            >
              <span className="thermostat-setup-legend__column-name-text">SERIAL NUMBER</span>
              <ColumnArrow value="SERIAL_NUMBER" sortValue={sortValue} isReversed={isReversed} />
            </button>
            <button
              className={
                sortValue === 'REGISTERED_DATE'
                  ? `${legendColumnClass} thermostat-setup-legend__column-name--selected desktop-only`
                  : `${legendColumnClass} desktop-only`
              }
              id="registered-date"
              onClick={() => this.updateSortValue('REGISTERED_DATE')}
            >
              <span className="thermostat-setup-legend__column-name-text">REGISTERED DATE</span>
              <ColumnArrow value="REGISTERED_DATE" sortValue={sortValue} isReversed={isReversed} />
            </button>
            <span className="thermostat-setup-legend__disconnected-time-column desktop-only">DISCONNECTED TIME</span>
          </div>
          {isFetching ? (
            <div className="thermostat-loader">
              <Spinner />
            </div>
          ) : (
            this.renderThermostats()
          )}
          <Pagination
            currentPage={this.state.currentPage}
            itemsPerPage={thermostatsPerPage}
            numberOfItems={allThermostats.length}
            onClick={this.changePage}
          />
        </div>
      </React.Fragment>
    );
  }
}

/* eslint-disable react/forbid-prop-types */
ThermostatSetup.propTypes = {
  allThermostats: PropTypes.arrayOf(
    PropTypes.shape({
      mode: PropTypes.string,
      state: PropTypes.string,
      uid_hash: PropTypes.string,
      fan_mode: PropTypes.string,
      direction: PropTypes.string,
      is_occupied: PropTypes.bool,
      unit_number: PropTypes.string,
      display_name: PropTypes.string,
      floor_number: PropTypes.number,
      serial_number: PropTypes.string,
      building_slug: PropTypes.string,
      is_fan_running: PropTypes.bool,
      thermostat_type: PropTypes.string,
      building_section: PropTypes.string,
      temperature_unit: PropTypes.string,
      connection_state: PropTypes.string,
      target_temperature: PropTypes.string,
      ambient_temperature: PropTypes.string,
      connection_timestamp: PropTypes.string,
      target_temperature_low: PropTypes.string,
      target_temperature_high: PropTypes.string,
      disconnection_timestamp: PropTypes.string,
      status_update_timestamp: PropTypes.string,
      temperature_satisfaction: PropTypes.string,
    }),
  ),
  selectedBuilding: PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    api_location: PropTypes.string,
    savings_start_datetime: PropTypes.string,
    navigation: PropTypes.arrayOf(
      PropTypes.shape({
        api_location: PropTypes.string,
        id: PropTypes.string,
        image: PropTypes.string,
        name: PropTypes.string,
        sub_categories: PropTypes.arrayOf(
          PropTypes.shape({
            api_location: PropTypes.string,
            data_api_location: PropTypes.string,
            id: PropTypes.string,
            name: PropTypes.string,
            url: PropTypes.string,
          }),
        ),
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  invalidSelectedBuildingError: PropTypes.string,
  clearRefreshedPin: PropTypes.func.isRequired,
  clearRestoreResults: PropTypes.func.isRequired,
  setThermostats: PropTypes.func.isRequired,
  clearLastRequest: PropTypes.func.isRequired,
  navigationParams: PropTypes.shape({
    selectedCategory: PropTypes.shape({
      api_location: PropTypes.string,
      id: PropTypes.string,
      image: PropTypes.string,
      name: PropTypes.string,
      sub_categories: PropTypes.arrayOf(
        PropTypes.shape({
          api_location: PropTypes.string,
          data_api_location: PropTypes.string,
          id: PropTypes.string,
          name: PropTypes.string,
          url: PropTypes.string,
        }),
      ),
      url: PropTypes.string,
    }),
    selectedSubCategory: PropTypes.shape({
      api_location: PropTypes.string,
      data_api_location: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  isFetching: PropTypes.bool.isRequired,
};

ThermostatSetup.defaultProps = {
  allThermostats: [],
  invalidSelectedBuildingError: undefined,
  navigationParams: {},
};

export function mapStateToProps(state) {
  return {
    allThermostats: state.thermostatReducer.allThermostats,
    selectedBuilding: state.buildingReducer.selectedBuilding,
    invalidSelectedBuildingError: state.buildingReducer.error,
    navigationParams: state.buildingReducer.navigationParams,
    isFetching: state.thermostatReducer.isFetching,
  };
}

const mapDispatchToProps = {
  setThermostats: ThermostatActions.setThermostats,
  clearRefreshedPin: ThermostatActions.clearRefreshedPin,
  clearRestoreResults: ThermostatActions.clearRestoreResults,
  clearLastRequest: NetworkActions.clearLastRequest,
};

export const ConnectedThermostatSetup = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ThermostatSetup);
